/**
 * useFamilyResourcesCalendar Hook
 *
 * Manages state and data for the family-based resources calendar view.
 * Maps events to family member columns based on labels and calendar mappings.
 */

import { useMemo, useCallback } from 'react';
import { FamilyResourceMapper } from '@/lib/family/FamilyResourceMapper';
import { ResourcesCalendarManager } from '@/lib/calendar/ResourcesCalendarManager';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { CalendarEvent } from '@/types/events';
import type {
  FamilyMemberResource,
  FamilyResourceCalendarEvent,
  AllDayEventsByFamilyMember,
} from '@/types/family-resource';

type UseFamilyResourcesCalendarParams = {
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  allEvents: CalendarEvent[];
  initialDate?: Date;
  onDateChange?: (date: Date) => void;
  meDisplayName?: string;
  selectedLabelIds?: string[];
};

export function useFamilyResourcesCalendar({
  familyMembers,
  calendarMappings,
  allEvents,
  initialDate,
  onDateChange,
  meDisplayName,
  selectedLabelIds = [],
}: UseFamilyResourcesCalendarParams) {
  // Use initialDate directly as controlled value
  const selectedDate = initialDate || ResourcesCalendarManager.getToday();

  // Find "Me" member ID for default assignment
  const meMemberId = useMemo(() => {
    const meMember = familyMembers.find(m => m.isMe);
    return meMember?.id || '';
  }, [familyMembers]);

  // Map family members to resources, filtered by selected labels
  const resources: FamilyMemberResource[] = useMemo(() => {
    const allResources = FamilyResourceMapper.mapMembersToResources(familyMembers, meDisplayName);
    // Filter resources when labels are selected and not all are selected
    const allSelected = selectedLabelIds.length === 0 ||
      selectedLabelIds.length >= familyMembers.length;
    if (allSelected) return allResources;
    return allResources.filter(r => selectedLabelIds.includes(r.id));
  }, [familyMembers, meDisplayName, selectedLabelIds]);

  // Filter events for selected date
  const dateKey = ResourcesCalendarManager.toDateKey(selectedDate);
  const filteredEvents = useMemo(() => {
    return FamilyResourceMapper.filterEventsForDate(allEvents, dateKey);
  }, [allEvents, dateKey]);

  // Separate all-day and timed events
  const { allDay: allDayEvents, timed: timedEvents } = useMemo(() => {
    return FamilyResourceMapper.separateAllDayEvents(filteredEvents);
  }, [filteredEvents]);

  // Group all-day events by family member
  const allDayEventsByMember: AllDayEventsByFamilyMember = useMemo(() => {
    if (!meMemberId) return new Map();
    return FamilyResourceMapper.groupAllDayEventsByMember(
      allDayEvents,
      familyMembers,
      calendarMappings,
      meMemberId
    );
  }, [allDayEvents, familyMembers, calendarMappings, meMemberId]);

  // Map timed events to family resource calendar events
  const familyTimedEvents: FamilyResourceCalendarEvent[] = useMemo(() => {
    if (!meMemberId) return [];
    return FamilyResourceMapper.mapEventsToFamilyResourceEvents(
      timedEvents,
      familyMembers,
      calendarMappings,
      meMemberId
    );
  }, [timedEvents, familyMembers, calendarMappings, meMemberId]);

  // Navigation handlers
  const goToPrevDay = useCallback(() => {
    const newDate = ResourcesCalendarManager.getPreviousDay(selectedDate);
    onDateChange?.(newDate);
  }, [selectedDate, onDateChange]);

  const goToNextDay = useCallback(() => {
    const newDate = ResourcesCalendarManager.getNextDay(selectedDate);
    onDateChange?.(newDate);
  }, [selectedDate, onDateChange]);

  const goToToday = useCallback(() => {
    const today = ResourcesCalendarManager.getToday();
    onDateChange?.(today);
  }, [onDateChange]);

  const goToDate = useCallback((date: Date) => {
    onDateChange?.(date);
  }, [onDateChange]);

  return {
    selectedDate,
    resources,
    timedEvents: familyTimedEvents,
    allDayEventsByMember,
    meMemberId,
    goToPrevDay,
    goToNextDay,
    goToToday,
    goToDate,
  };
}
